import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, Columns3 } from "lucide-react";
import { AggregateFunction, DatasetFieldInfo } from "@forgepoint/shared";
import { useDatasetExplorerStore } from "../datasetExplorerStore";

const AGGREGATE_OPTIONS: AggregateFunction[] = [
  "sum",
  "count",
  "count_distinct",
  "avg",
  "min",
  "max",
];

/** Maps field types to short display labels. */
const TYPE_LABELS: Record<string, string> = {
  decimal: "num",
  integer: "int",
  real: "num",
  text: "txt",
  boolean: "bool",
  timestamp: "ts",
  date: "date",
  uuid: "id",
};

/**
 * Multi-select field list with optional aggregation configuration.
 * Fields are grouped by prefix (root first, then enabled joins).
 */
export default function FieldPicker() {
  const schemas = useDatasetExplorerStore((s) => s.schemas);
  const selectedDataset = useDatasetExplorerStore((s) => s.selectedDataset);
  const enabledJoins = useDatasetExplorerStore((s) => s.enabledJoins);
  const selectedFields = useDatasetExplorerStore((s) => s.selectedFields);
  const addField = useDatasetExplorerStore((s) => s.addField);
  const removeField = useDatasetExplorerStore((s) => s.removeField);
  const updateField = useDatasetExplorerStore((s) => s.updateField);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const currentSchema = schemas.find((s) => s.dataset === selectedDataset);
  if (!currentSchema) {
    return null;
  }

  const availablePrefixes = [
    currentSchema.rootPrefix,
    ...Array.from(enabledJoins),
  ];

  const groupedFields = availablePrefixes.reduce<
    Record<string, DatasetFieldInfo[]>
  >((acc, prefix) => {
    const fields = currentSchema.fields.filter((f) =>
      f.field.startsWith(`${prefix}.`),
    );
    if (fields.length > 0) {
      acc[prefix] = fields;
    }
    return acc;
  }, {});

  const selectedFieldSet = new Set(selectedFields.map((f) => f.field));

  /**
   * @param fieldRef - The field reference string to find
   * @returns The index in selectedFields, or -1
   */
  function findFieldIndex(fieldRef: string): number {
    return selectedFields.findIndex((f) => f.field === fieldRef);
  }

  /**
   * Toggles the collapsed state of a field group.
   * @param prefix - The group prefix to toggle
   */
  function toggleGroup(prefix: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) {
        next.delete(prefix);
      } else {
        next.add(prefix);
      }
      return next;
    });
  }

  /**
   * @param fieldRef - The field reference to toggle
   */
  function handleToggle(fieldRef: string) {
    if (selectedFieldSet.has(fieldRef)) {
      const idx = findFieldIndex(fieldRef);
      if (idx !== -1) {
        removeField(idx);
      }
    } else {
      addField({ field: fieldRef });
    }
  }

  /**
   * @param fieldRef - The field reference to update
   * @param aggregate - The new aggregate function, or undefined to clear
   */
  function handleAggregateChange(
    fieldRef: string,
    aggregate: string | undefined,
  ) {
    const idx = findFieldIndex(fieldRef);
    if (idx === -1) {
      return;
    }
    const existing = selectedFields[idx];
    if (aggregate === "none" || !aggregate) {
      updateField(idx, { field: existing.field });
    } else {
      updateField(idx, {
        field: existing.field,
        aggregate: aggregate as AggregateFunction,
        alias: existing.alias,
      });
    }
  }

  /**
   * @param fieldRef - The field reference to update
   * @param alias - The new alias value
   */
  function handleAliasChange(fieldRef: string, alias: string) {
    const idx = findFieldIndex(fieldRef);
    if (idx === -1) {
      return;
    }
    const existing = selectedFields[idx];
    updateField(idx, {
      field: existing.field,
      aggregate: existing.aggregate,
      alias: alias || undefined,
    });
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Columns3 className="h-3 w-3" />
        Fields
        {selectedFields.length > 0 && (
          <Badge
            variant="secondary"
            className="ml-auto text-[10px] px-1.5 py-0 h-4 font-normal"
          >
            {selectedFields.length}
          </Badge>
        )}
      </Label>
      <div className="space-y-1">
        {Object.entries(groupedFields).map(([prefix, fields]) => {
          const selectedCount = fields.filter((f) =>
            selectedFieldSet.has(f.field),
          ).length;

          return (
            <Collapsible
              key={prefix}
              open={!collapsedGroups.has(prefix)}
              onOpenChange={() => toggleGroup(prefix)}
            >
              <CollapsibleTrigger className="flex w-full items-center gap-1 py-1 px-1 rounded hover:bg-muted/50 transition-colors group">
                <ChevronRight
                  className={`h-3 w-3 text-muted-foreground transition-transform ${
                    !collapsedGroups.has(prefix) ? "rotate-90" : ""
                  }`}
                />
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {prefix}
                </span>
                {selectedCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-auto text-[10px] px-1.5 py-0 h-4 font-normal"
                  >
                    {selectedCount}
                  </Badge>
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-0.5 mt-0.5">
                  {fields.map((field) => {
                    const isSelected = selectedFieldSet.has(field.field);
                    const idx = findFieldIndex(field.field);
                    const selectedEntry =
                      idx !== -1 ? selectedFields[idx] : undefined;
                    const columnName = field.field.split(".")[1];

                    return (
                      <div key={field.field}>
                        <div
                          className={`flex items-center gap-2 rounded px-1.5 py-1 transition-colors ${
                            isSelected
                              ? "bg-primary/5 hover:bg-primary/8"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <Checkbox
                            id={`field-${field.field}`}
                            checked={isSelected}
                            onCheckedChange={() => handleToggle(field.field)}
                          />
                          <label
                            htmlFor={`field-${field.field}`}
                            className="text-sm cursor-pointer flex-1 truncate"
                          >
                            {columnName}
                          </label>
                          <span className="text-[10px] text-muted-foreground/60 font-mono shrink-0">
                            {TYPE_LABELS[field.type] ?? field.type}
                          </span>
                        </div>
                        {isSelected && field.aggregatable && (
                          <div className="ml-7 mr-1 flex items-center gap-1.5 py-1">
                            <Select
                              value={selectedEntry?.aggregate ?? "none"}
                              onValueChange={(v) =>
                                handleAggregateChange(field.field, v)
                              }
                            >
                              <SelectTrigger className="h-7 text-xs w-32 bg-background">
                                <SelectValue placeholder="Aggregate" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  No aggregate
                                </SelectItem>
                                {AGGREGATE_OPTIONS.map((agg) => (
                                  <SelectItem key={agg} value={agg}>
                                    {agg.toUpperCase()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {selectedEntry?.aggregate && (
                              <Input
                                className="h-7 text-xs flex-1 bg-background"
                                placeholder="Alias"
                                value={selectedEntry.alias ?? ""}
                                onChange={(e) =>
                                  handleAliasChange(field.field, e.target.value)
                                }
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}
